import React, { useState, useEffect } from 'react';
import { Upload, XCircle, PiggyBank } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField, InlineEditText } from '../shared/InlineEditField';
import { UpgradePrompt } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { Retirement401k } from '../../types';

interface Retirement401kWidgetProps {
  onAdd: () => void;
  onUpgradeClick: () => void;
  onImportCSV: () => void;
  variant?: 'card' | 'page';
}

export const Retirement401kWidget: React.FC<Retirement401kWidgetProps> = ({
  onAdd,
  onUpgradeClick,
  onImportCSV,
  variant = 'card',
}) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { total401k, update401kHolding, handleDelete401kHolding, handleClearAll401kHoldings, handleBatchDelete } = useFinancialData();
  const { visible401kHoldings, hasHidden401k, hidden401kCount, isAt401kLimit } =
    useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit, completeEditText } =
    useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('401k');
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDelete401kHolding(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('401k', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, visible401kHoldings.length, handleBatchDeleteAsync);

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) editMode.exitEditMode();
  }, [collapsed, editMode]);

  const handleSave = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await update401kHolding(index, 'balance', value, sheetId);
    }
  };

  const handleSaveText = async (index: number, field: 'fundName' | 'ticker') => {
    const { value, isValid } = completeEditText();
    if (isValid && sheetId) {
      await update401kHolding(index, field, value, sheetId);
    } else {
      cancelEdit();
    }
  };

  const handleClearAll = async () => {
    if (!sheetId || isClearing) return;
    setIsClearing(true);
    try {
      await handleClearAll401kHoldings(sheetId);
    } finally {
      setIsClearing(false);
      setConfirmClearAll(false);
    }
  };

  const handleAddClick = () => {
    if (isAt401kLimit) {
      onUpgradeClick();
    } else {
      onAdd();
    }
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="401k Retirement"
        icon={<PiggyBank size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={visible401kHoldings.length}
            />
            {visible401kHoldings.length > 0 && (
              <button
                onClick={() => setConfirmClearAll(true)}
                className={`p-1.5 rounded-lg transition-all ${
                  darkMode
                    ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/20'
                    : 'text-slate-500 hover:text-red-500 hover:bg-red-500/10'
                }`}
                title="Remove all holdings"
              >
                <XCircle size={18} />
              </button>
            )}
            <button
              onClick={onImportCSV}
              className={`p-1.5 rounded-lg transition-all ${
                darkMode
                  ? 'text-slate-400 hover:text-violet-400 hover:bg-violet-500/20'
                  : 'text-slate-500 hover:text-violet-600 hover:bg-violet-100'
              }`}
              title="Import CSV"
            >
              <Upload size={18} />
            </button>
            <AddButton onClick={handleAddClick} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('401k')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={visible401kHoldings.length}
          allSelected={editMode.selectedIndices.size === visible401kHoldings.length}
          onSelectAll={() => editMode.selectAll(visible401kHoldings.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      {/* Clear All Confirmation */}
      {confirmClearAll && (
        <div
          className={`mb-4 p-3 rounded-lg border ${
            darkMode
              ? 'bg-red-500/10 border-red-500/30'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
            Remove all {visible401kHoldings.length} holdings?
          </p>
          <p className={`text-xs mb-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
            This will clear your entire 401k portfolio from the sheet. This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleClearAll}
              disabled={isClearing}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                darkMode
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              } ${isClearing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isClearing ? 'Removing...' : 'Yes, Remove All'}
            </button>
            <button
              onClick={() => setConfirmClearAll(false)}
              disabled={isClearing}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                darkMode
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {visible401kHoldings.length === 0 ? (
          <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <p className="text-sm">No 401k holdings yet</p>
            <p className="text-xs mt-1">Add manually or import a CSV from your provider</p>
          </div>
        ) : (
          <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2 md:[&_th]:px-1 md:[&_td]:px-1" style={{ minWidth: 500 }}>
            <thead>
              <tr
                className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${
                  darkMode ? 'border-white/10' : 'border-slate-200'
                }`}
              >
                {editMode.isEditMode && <th className="w-8 pb-2" />}
                <th className="text-left pb-2 whitespace-nowrap">Fund</th>
                <th className="text-left pb-2 whitespace-nowrap w-[12%]">Ticker</th>
                <th className="text-right pb-2 whitespace-nowrap w-[13%]">Shares</th>
                <th className="text-right pb-2 whitespace-nowrap w-[15%]">Value</th>
                <th className="text-right pb-2 whitespace-nowrap w-[18%]">Type</th>
              </tr>
            </thead>
            <tbody>
              {visible401kHoldings.map((holding: Retirement401k, idx: number) => (
                <tr
                  key={idx}
                  className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${
                    darkMode ? 'border-white/10' : 'border-slate-100'
                  }`}
                >
                  {editMode.isEditMode && (
                    <td className="py-2 pr-2">
                      <EditModeCheckbox
                        checked={editMode.selectedIndices.has(idx)}
                        onToggle={() => editMode.toggleIndex(idx)}
                      />
                    </td>
                  )}
                  <td className="py-2 font-semibold max-w-[140px] truncate" title={holding.fundName}>
                    <InlineEditText
                      value={holding.fundName}
                      isEditing={isEditing('retirement401k', idx, 'fundName')}
                      editValue={editValue}
                      onEditValueChange={setEditValue}
                      onStartEdit={() => startEdit('retirement401k', idx, 'fundName', holding.fundName)}
                      onSave={() => handleSaveText(idx, 'fundName')}
                      onCancel={cancelEdit}
                      editable={holding.editable}
                      width="w-full"
                    />
                  </td>
                  <td className="py-2 font-mono">
                    <InlineEditText
                      value={holding.ticker || '-'}
                      isEditing={isEditing('retirement401k', idx, 'ticker')}
                      editValue={editValue}
                      onEditValueChange={setEditValue}
                      onStartEdit={() => startEdit('retirement401k', idx, 'ticker', holding.ticker)}
                      onSave={() => handleSaveText(idx, 'ticker')}
                      onCancel={cancelEdit}
                      editable={holding.editable}
                      width="w-full"
                    />
                  </td>
                  <td className="text-right">
                    {privacyMode ? '•••' : formatNumber(holding.shares, 3)}
                  </td>
                  <td className="text-right">
                    <InlineEditField
                      value={holding.balance}
                      isEditing={isEditing('retirement401k', idx, 'balance')}
                      editValue={editValue}
                      onEditValueChange={setEditValue}
                      onStartEdit={() =>
                        startEdit('retirement401k', idx, 'balance', holding.balance)
                      }
                      onSave={() => handleSave(idx)}
                      onCancel={cancelEdit}
                      editable={holding.editable}
                      variant="positive"
                      width="w-full"
                      privacyMode={privacyMode}
                    />
                  </td>
                  <td className={`text-right ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {holding.accountType}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {hasHidden401k && (
          <div className="mt-4">
            <UpgradePrompt
              count={hidden401kCount}
              type="holdings"
              onClick={onUpgradeClick}
            />
          </div>
        )}
      </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm px-2 md:px-1">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Total 401k
          </span>
          <span className={`${darkMode ? 'text-violet-400' : 'text-violet-600'} text-right`}>
            {formatCurrency(total401k, privacyMode)}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
